import * as React from 'react';

import {
  DocumentType,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { cn, ContentCard, Loading } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { useGenerateDocumentsAndSendEmail } from '../../hooks/useGenerateDocumentsAndSendEmail';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { LoadRecord } from '../../types/load';
import { FloatingActionBar } from '../_shared/FloatingActionBar';

import { SalesOrderTabDocumentsBookingSection } from './SalesOrderTabDocumentsBookingSection';
import { SalesOrderTabDocumentsRow } from './SalesOrderTabDocumentsRow';

import { $remoteSalesOrderLoads } from '~/modules/trade/_state/salesOrderDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SalesOrderRecord } from '~/modules/trade/types/salesOrder';

type Props = {
  salesOrder?: SalesOrderRecord | null;
  variant?: 'default' | 'embedded';
};

const DOCUMENT_DISPLAY_NAMES: Partial<Record<DocumentType, string>> = {
  Invoice: 'Invoice',
  PackingList: 'Packing List',
  BillOfLading: 'Bill of Lading',
  SalesOrder: 'Sales Order',
  PurchaseOrder: 'Purchase Order',
  ShippingSummary: 'Shipping Summary',
  BookingInvoice: 'Booking Invoice',
  LoadInvoice: 'Load Invoice',
};

export const SalesOrderTabDocuments: React.FC<Props> = ({
  salesOrder,
  variant = 'default',
}) => {
  const t = useTranslations();

  const isVoided = salesOrder?.status === enumSalesOrderStatus.Voided;

  // Selection state
  const [selectedDocumentsByLoad, setSelectedDocumentsByLoad] = React.useState<
    Record<string, Record<DocumentType, boolean>>
  >({});
  const [selectedSalesOrderDocuments, setSelectedSalesOrderDocuments] =
    React.useState<{
      salesOrder: boolean;
      shippingSummary: boolean;
    }>({
      salesOrder: false,
      shippingSummary: false,
    });
  const [selectedBookingInvoices, setSelectedBookingInvoices] = React.useState<
    Record<string, boolean>
  >({});
  const [isActionBarOpen, setIsActionBarOpen] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Fetch all loads for this sales order
  const { data: loadsData, isLoading } = useAtomValue($remoteSalesOrderLoads);

  // Hooks for printing and emailing
  const { print, isPrinting } = usePrintWithBrowser();
  const { sendDocumentsAndEmail, isPending } =
    useGenerateDocumentsAndSendEmail();

  // Group loads by booking
  const groupedLoadsByBooking = React.useMemo(() => {
    const grouped = new Map<
      string,
      (Pick<
        Partial<LoadRecord>,
        'loadNumber' | 'bookingNumber' | 'invoiceId'
      > & {
        id: string;
      })[]
    >();

    const unassignedLoads: (Pick<
      Partial<LoadRecord>,
      'loadNumber' | 'bookingNumber' | 'invoiceId'
    > & { id: string })[] = [];

    loadsData?.loads?.forEach((load) => {
      if (load.bookingId) {
        if (!grouped.has(load.bookingId)) {
          grouped.set(load.bookingId, []);
        }
        grouped.get(load.bookingId)?.push({ ...load, id: load.id as string });
      } else {
        unassignedLoads.push(load);
      }
    });

    // Include bookings from sales order that have no loads in this SO
    salesOrder?.bookings?.forEach((booking) => {
      if (!grouped.has(booking.id)) {
        grouped.set(booking.id, []);
      }
    });

    // Add unassigned loads as a separate group if they exist
    if (unassignedLoads.length > 0) {
      grouped.set('unassigned', unassignedLoads);
    }

    return grouped;
  }, [loadsData?.loads, salesOrder?.bookings]);

  // Count selected documents
  const selectedLoadDocuments = Object.values(selectedDocumentsByLoad).reduce(
    (acc, loadDocs) => acc + Object.values(loadDocs).filter(Boolean).length,
    0,
  );

  const selectedSalesOrderDocumentsCount = Object.values(
    selectedSalesOrderDocuments,
  ).filter(Boolean).length;

  const selectedBookingInvoiceCount = Object.values(
    selectedBookingInvoices,
  ).filter(Boolean).length;

  const selectedCount =
    selectedLoadDocuments +
    selectedSalesOrderDocumentsCount +
    selectedBookingInvoiceCount;

  React.useEffect(() => {
    setIsActionBarOpen(selectedCount > 0);
  }, [selectedCount]);

  const handleCloseActionBar = () => {
    setIsActionBarOpen(false);
    setSelectedDocumentsByLoad({});
    setSelectedSalesOrderDocuments({
      salesOrder: false,
      shippingSummary: false,
    });
    setSelectedBookingInvoices({});
  };

  const handleSalesOrderDocumentSelect = React.useCallback(
    (documentType: 'salesOrder' | 'shippingSummary', checked: boolean) => {
      setSelectedSalesOrderDocuments((prev) => ({
        ...prev,
        [documentType]: checked,
      }));
    },
    [],
  );

  const handlePrintDocuments = React.useCallback(async () => {
    const documentsToPrint: Array<{
      documentType: DocumentType;
      bookingId?: string;
      loadId?: string;
      salesOrderId?: string;
      invoiceId?: string;
    }> = [];

    // Add load documents
    Object.entries(selectedDocumentsByLoad).forEach(([loadId, loadDocs]) => {
      Object.entries(loadDocs).forEach(([documentType, checked]) => {
        if (checked) {
          // Find the load to get the invoiceId
          const load = loadsData?.loads?.find((l) => l.id === loadId);
          documentsToPrint.push({
            documentType: documentType as DocumentType,
            loadId,
            invoiceId:
              documentType === 'Invoice'
                ? (load?.invoiceId ?? undefined)
                : undefined,
          });
        }
      });
    });

    // Add sales order documents
    if (selectedSalesOrderDocuments.salesOrder && salesOrder?.id) {
      documentsToPrint.push({
        documentType: 'SalesOrder' as DocumentType,
        salesOrderId: salesOrder.id,
      });
    }

    if (selectedSalesOrderDocuments.shippingSummary && salesOrder?.id) {
      documentsToPrint.push({
        documentType: 'ShippingSummary' as DocumentType,
        salesOrderId: salesOrder.id,
      });
    }

    // Add booking invoice documents
    Object.entries(selectedBookingInvoices).forEach(
      ([bookingId, isSelected]) => {
        if (isSelected) {
          documentsToPrint.push({
            documentType: 'BookingInvoice' as DocumentType,
            bookingId,
          });
        }
      },
    );

    if (documentsToPrint.length === 0) {
      return;
    }

    try {
      await print({
        documents: documentsToPrint,
      });

      // Clear selection after successful print
      setSelectedDocumentsByLoad({});
      setSelectedSalesOrderDocuments({
        salesOrder: false,
        shippingSummary: false,
      });
      setSelectedBookingInvoices({});
    } catch (error) {
      console.error('Failed to print documents:', error);
    }
  }, [
    selectedDocumentsByLoad,
    selectedSalesOrderDocuments,
    selectedBookingInvoices,
    print,
    loadsData?.loads,
    salesOrder?.id,
  ]);

  const handleDownloadDocuments = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      const documentsToDownload: Array<{
        documentType: DocumentType;
        bookingId?: string;
        loadId?: string;
        salesOrderId?: string;
        invoiceId?: string;
      }> = [];

      // Add load documents
      Object.entries(selectedDocumentsByLoad).forEach(([loadId, loadDocs]) => {
        Object.entries(loadDocs).forEach(([documentType, checked]) => {
          if (checked) {
            // Find the load to get the invoiceId
            const load = loadsData?.loads?.find((l) => l.id === loadId);
            documentsToDownload.push({
              documentType: documentType as DocumentType,
              loadId,
              invoiceId:
                documentType === 'Invoice'
                  ? (load?.invoiceId ?? undefined)
                  : undefined,
            });
          }
        });
      });

      // Add sales order documents
      if (selectedSalesOrderDocuments.salesOrder && salesOrder?.id) {
        documentsToDownload.push({
          documentType: 'SalesOrder' as DocumentType,
          salesOrderId: salesOrder.id,
        });
      }

      if (selectedSalesOrderDocuments.shippingSummary && salesOrder?.id) {
        documentsToDownload.push({
          documentType: 'ShippingSummary' as DocumentType,
          salesOrderId: salesOrder.id,
        });
      }

      // Add booking invoice documents
      Object.entries(selectedBookingInvoices).forEach(
        ([bookingId, isSelected]) => {
          if (isSelected) {
            documentsToDownload.push({
              documentType: 'BookingInvoice' as DocumentType,
              bookingId,
            });
          }
        },
      );

      if (documentsToDownload.length === 0) {
        return;
      }

      await print({
        documents: documentsToDownload,
        action: 'download',
      });

      // Clear selection after successful download
      setSelectedDocumentsByLoad({});
      setSelectedSalesOrderDocuments({
        salesOrder: false,
        shippingSummary: false,
      });
      setSelectedBookingInvoices({});
    } catch (error) {
      console.error('Failed to download documents:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [
    selectedDocumentsByLoad,
    selectedSalesOrderDocuments,
    selectedBookingInvoices,
    print,
    loadsData?.loads,
    salesOrder?.id,
  ]);

  const handleSendEmail = React.useCallback(
    async (emails: string[]) => {
      if (emails.length === 0) {
        return;
      }

      const documentsToEmail: Array<{
        documentType: DocumentType;
        bookingId?: string;
        loadId?: string;
        salesOrderId?: string;
      }> = [];

      // Add load documents
      Object.entries(selectedDocumentsByLoad).forEach(([loadId, loadDocs]) => {
        Object.entries(loadDocs).forEach(([documentType, checked]) => {
          if (checked) {
            documentsToEmail.push({
              documentType: documentType as DocumentType,
              loadId,
            });
          }
        });
      });

      // Add sales order documents
      if (selectedSalesOrderDocuments.salesOrder && salesOrder?.id) {
        documentsToEmail.push({
          documentType: 'SalesOrder' as DocumentType,
          salesOrderId: salesOrder.id,
        });
      }

      if (selectedSalesOrderDocuments.shippingSummary && salesOrder?.id) {
        documentsToEmail.push({
          documentType: 'ShippingSummary' as DocumentType,
          salesOrderId: salesOrder.id,
        });
      }

      // Add booking invoice documents
      Object.entries(selectedBookingInvoices).forEach(
        ([bookingId, isSelected]) => {
          if (isSelected) {
            documentsToEmail.push({
              documentType: 'BookingInvoice' as DocumentType,
              bookingId,
            });
          }
        },
      );

      if (documentsToEmail.length === 0) {
        return;
      }

      try {
        await sendDocumentsAndEmail({
          documents: documentsToEmail,
          email: {
            to: emails,
            subject: t.salesOrderDetail.documents.email.subject({
              salesOrderNumber: salesOrder?.documentNumber ?? '–',
            }),
            textBody: t.salesOrderDetail.documents.email.textBody({
              salesOrderNumber: salesOrder?.documentNumber ?? '–',
              documentTypes: documentsToEmail
                .map((doc) => DOCUMENT_DISPLAY_NAMES[doc.documentType])
                .join(', '),
            }),
            htmlBody: t.salesOrderDetail.documents.email.htmlBody({
              salesOrderNumber: salesOrder?.documentNumber ?? '–',
              documentTypes: documentsToEmail
                .map((doc) => DOCUMENT_DISPLAY_NAMES[doc.documentType])
                .join(', '),
            }),
          },
        });

        // Clear selection after successful email send
        setSelectedDocumentsByLoad({});
        setSelectedSalesOrderDocuments({
          salesOrder: false,
          shippingSummary: false,
        });
        setSelectedBookingInvoices({});
      } catch (error) {
        // Error handling is done in the hook
        console.error('Failed to send documents via email:', error);
      }
    },
    [
      selectedDocumentsByLoad,
      selectedSalesOrderDocuments,
      selectedBookingInvoices,
      sendDocumentsAndEmail,
      salesOrder?.documentNumber,
      salesOrder?.id,
      t,
    ],
  );

  const handlePostPrint = React.useCallback(() => {
    setSelectedDocumentsByLoad({});
    setSelectedSalesOrderDocuments({
      salesOrder: false,
      shippingSummary: false,
    });
    setSelectedBookingInvoices({});
  }, []);

  if (!salesOrder) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-white p-6">
          <div className="py-8 text-center">
            <p className="text-neutral/50">Sales order not found</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div
      className={cn('@SalesOrderTabDocuments', variant !== 'embedded' && 'p-5')}
    >
      <div className="flex h-full flex-1 flex-col">
        <div className="w-full">
          {/* Sales Order Documents Section */}
          {!isVoided && (
            <ContentCard
              className={cn(
                variant === 'embedded' &&
                  'rounded-none rounded-b-lg border-none',
              )}
            >
              <SalesOrderTabDocumentsRow
                selectedDocuments={selectedSalesOrderDocuments}
                onDocumentSelect={handleSalesOrderDocumentSelect}
              />
            </ContentCard>
          )}

          {/* Booking Documents Sections */}
          {Array.from(groupedLoadsByBooking.entries()).map(
            ([bookingId, loads]) => (
              <SalesOrderTabDocumentsBookingSection
                key={bookingId}
                bookingId={bookingId}
                loads={loads}
                selectedDocuments={selectedDocumentsByLoad}
                onSelectedDocumentsChange={setSelectedDocumentsByLoad}
                bookingInvoiceSelected={
                  selectedBookingInvoices[bookingId] ?? false
                }
                onBookingInvoiceSelect={(checked) =>
                  setSelectedBookingInvoices((prev) => ({
                    ...prev,
                    [bookingId]: checked,
                  }))
                }
                bookingNumber={
                  salesOrder?.bookings?.find((b) => b.id === bookingId)
                    ?.bookingNumber
                }
              />
            ),
          )}

          {/* No loads message */}
          {Array.from(groupedLoadsByBooking.keys()).length === 0 && (
            <ContentCard
              className={cn(
                'mb-6 w-full',
                variant === 'embedded' && 'rounded-none border-none',
              )}
            >
              <div className="text-center">
                <p className="text-neutral/50">
                  {t.salesOrderDetail.documents.noLoads}
                </p>
              </div>
            </ContentCard>
          )}
        </div>

        {/* Floating Action Bar */}
        {selectedCount > 0 && (
          <div className="pointer-events-none absolute bottom-0 left-0 flex w-full justify-center">
            <div className="pointer-events-auto">
              <FloatingActionBar
                selectedCount={selectedCount}
                isOpen={isActionBarOpen}
                onClose={handleCloseActionBar}
                onPrint={handlePrintDocuments}
                onDownload={handleDownloadDocuments}
                onSendEmail={handleSendEmail}
                onPostPrint={handlePostPrint}
                isPrinting={isPrinting}
                isDownloading={isDownloading}
                isEmailSending={isPending}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
